import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Calendar, ArrowLeft, X, Plus, CalendarDays } from 'lucide-react';
import { format, addMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isBefore, parseISO } from 'date-fns';
import { toast } from 'sonner';

export default function ArtistCalendar() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [blockedDates, setBlockedDates] = useState([]);
  const [bookedDates, setBookedDates] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!user) return;
    if (!['artist', 'admin', 'owner', 'overseer'].includes(user.role)) {
      navigate('/');
      return;
    }
    fetchBlockedDates();
    fetchBookedDates();
  }, [user]);

  const fetchBookedDates = async () => {
    try {
      const res = await apiClient.get('/artist/booked-dates');
      setBookedDates(Array.isArray(res.data) ? res.data : []);
    } catch { /* non-fatal */ }
  };

  const fetchBlockedDates = async () => {
    try {
      const response = await apiClient.get('/artist/availability');
      setBlockedDates(response.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockDate = async () => {
    if (!selectedDate) return;
    
    try {
      await apiClient.post('/artist/availability', {
        blocked_date: selectedDate,
        reason: reason || null
      });
      toast.success('Date blocked successfully');
      setSelectedDate(null);
      setReason('');
      fetchBlockedDates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block date');
    }
  };

  const handleUnblockDate = async (date) => {
    try {
      await apiClient.delete(`/artist/availability/${date}`);
      toast.success('Date unblocked successfully');
      fetchBlockedDates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unblock date');
    }
  };

  const isDateBlocked = (dateStr) => {
    return blockedDates.some(d => d.blocked_date === dateStr);
  };

  const getBlockedReason = (dateStr) => {
    const blocked = blockedDates.find(d => d.blocked_date === dateStr);
    return blocked?.reason || '';
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad the beginning of the month
  const startDayOfWeek = monthStart.getDay();
  const paddedDays = Array(startDayOfWeek).fill(null).concat(daysInMonth);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative pt-20">
      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/artist-dashboard')}
            className="hover:bg-white/10"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Button>
          <div>
            <h1 className="text-3xl font-bold gradient-text">Availability Calendar</h1>
            <p className="text-white/60">Manage your available dates for bookings</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <Card className="glass-default border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="text-violet-400" />
                  {format(currentMonth, 'MMMM yyyy')}
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}
                    className="hover:bg-white/10"
                  >
                    ←
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentMonth(new Date())}
                    className="hover:bg-white/10"
                  >
                    Today
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                    className="hover:bg-white/10"
                  >
                    →
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-2 mb-4">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center text-white/60 text-sm font-medium py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-2">
                  {paddedDays.map((day, idx) => {
                    if (!day) {
                      return <div key={`empty-${idx}`} className="aspect-square" />;
                    }

                    const dateStr = format(day, 'yyyy-MM-dd');
                    const blocked = isDateBlocked(dateStr);
                    const bookedOn = bookedDates.find(b => b.date === dateStr);
                    const past = isBefore(day, new Date()) && !isToday(day);
                    const today = isToday(day);

                    return (
                      <button
                        key={dateStr}
                        onClick={() => {
                          if (past) return;
                          if (bookedOn) {
                            toast.info(`📅 You have a ${bookedOn.status} booking on this date`);
                            return;
                          }
                          if (blocked) {
                            handleUnblockDate(dateStr);
                          } else {
                            setSelectedDate(dateStr);
                          }
                        }}
                        disabled={past}
                        className={`
                          aspect-square rounded-xl flex flex-col items-center justify-center text-sm
                          transition-all relative group
                          ${past ? 'text-white/20 cursor-not-allowed' : 'hover:bg-white/10 cursor-pointer'}
                          ${blocked ? 'bg-red-500/30 text-red-300' : ''}
                          ${bookedOn ? (bookedOn.status === 'confirmed' ? 'bg-green-500/30 text-green-200' : 'bg-yellow-500/30 text-yellow-200') : ''}
                          ${today ? 'ring-2 ring-violet-500' : ''}
                          ${selectedDate === dateStr ? 'bg-violet-500/50' : ''}
                        `}
                      >
                        <span className="font-medium">{format(day, 'd')}</span>
                        {bookedOn && (
                          <span className={`text-[8px] font-bold uppercase leading-none mt-0.5 ${bookedOn.status === 'confirmed' ? 'text-green-300' : 'text-yellow-300'}`}>
                            Booked
                          </span>
                        )}
                        {blocked && !bookedOn && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                            <X size={10} />
                          </span>
                        )}
                        {blocked && getBlockedReason(dateStr) && (
                          <span className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-xs rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap z-10">
                            {getBlockedReason(dateStr)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-6 mt-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-500/30 rounded" />
                    <span className="text-white/60">Blocked</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-500/30 rounded" />
                    <span className="text-white/60">Booked (confirmed)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-yellow-500/30 rounded" />
                    <span className="text-white/60">Booked (pending)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 ring-2 ring-violet-500 rounded" />
                    <span className="text-white/60">Today</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-white/20 rounded" />
                    <span className="text-white/60">Available</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Block Date Panel */}
          <div>
            <Card className="glass-default border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="text-violet-400" />
                  Block a Date
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-white/60 text-sm">
                  Click on a date in the calendar to select it, then optionally add a reason and confirm.
                </p>
                
                {selectedDate ? (
                  <>
                    <div className="p-4 bg-violet-500/20 rounded-xl">
                      <p className="text-sm text-white/60">Selected Date</p>
                      <p className="text-xl font-bold">{format(parseISO(selectedDate), 'MMMM d, yyyy')}</p>
                    </div>
                    
                    <Input
                      placeholder="Reason (optional)"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="bg-white/5 border-white/10"
                    />
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={handleBlockDate}
                        className="flex-1 bg-red-600 hover:bg-red-500"
                      >
                        Block Date
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setSelectedDate(null);
                          setReason('');
                        }}
                        className="hover:bg-white/10"
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-white/40">
                    <Calendar size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Select a date from the calendar</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Blocked Dates List */}
            <Card className="glass-default border-white/10 mt-6">
              <CardHeader>
                <CardTitle>Blocked Dates ({blockedDates.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {blockedDates.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {blockedDates.map((blocked) => (
                      <div
                        key={blocked.blocked_date}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                      >
                        <div>
                          <p className="font-medium">
                            {format(parseISO(blocked.blocked_date), 'MMM d, yyyy')}
                          </p>
                          {blocked.reason && (
                            <p className="text-sm text-white/60">{blocked.reason}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnblockDate(blocked.blocked_date)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-white/60 py-4">No blocked dates</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
